import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

function ScrollToTop() {
    const { pathname, search, hash, state } = useLocation();

    useEffect(() => {
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }

        // 'auto' hereda scroll-behavior:smooth del CSS global del sitio -- aqui
        // se quiere una posicion inmediata y determinista, no una animacion.
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, []);

    useEffect(() => {
        if (hash) {
            // El elemento del anchor (p.ej. #alertas) puede no estar todavia en
            // el DOM en la primera pasada tras un cambio de pagina, y el listado
            // de productos (scroll infinito, ProductList.js) puede seguir
            // creciendo bastante despues de eso -- un calendario de reintentos
            // fijo (antes: 0/150/400/800ms) se rinde antes de que el listado
            // termine de crecer, y el usuario se queda parado en una posicion
            // intermedia sin relacion con la seccion pedida (reproducido en
            // produccion: la pagina seguia casi cuadruplicando su altura mas
            // alla de los 800ms). En vez de un calendario fijo, se persigue la
            // posicion real del elemento con un polling corto (setTimeout, no
            // requestAnimationFrame -- rAF se pausa en pestañas sin foco/ocultas,
            // y esta correccion tiene que funcionar aunque la pestaña no este en
            // primer plano en ese instante) hasta que deja de moverse varias
            // comprobaciones seguidas, con un tope de seguridad de 4s por si el
            // elemento no existe o no se estabiliza.
            const id = hash.replace('#', '');
            let cancelled = false;
            let timeoutId;
            let lastTop = null;
            let stableChecks = 0;
            const deadline = Date.now() + 4000;

            // Si el usuario ya esta interactuando (toca o hace scroll el mismo),
            // se deja de forzar el scroll para no pelearle la posicion.
            const cancel = () => { cancelled = true; };
            window.addEventListener('touchstart', cancel, { once: true, passive: true });
            window.addEventListener('wheel', cancel, { once: true, passive: true });

            const track = () => {
                if (cancelled) {
                    return;
                }

                const element = document.getElementById(id);
                if (element) {
                    const top = element.getBoundingClientRect().top;
                    if (lastTop === null || Math.abs(top - lastTop) > 4) {
                        // 'instant', no 'auto': el CSS global del sitio tiene
                        // scroll-behavior:smooth, y con 'auto' cada correccion de
                        // este bucle reiniciaba una animacion smooth sobre la
                        // anterior sin llegar nunca a asentarse.
                        element.scrollIntoView({ behavior: 'instant', block: 'start' });
                        stableChecks = 0;
                    } else {
                        stableChecks += 1;
                    }
                    lastTop = top;
                }

                if (!cancelled && stableChecks < 4 && Date.now() < deadline) {
                    timeoutId = setTimeout(track, 100);
                }
            };

            track();

            return () => {
                cancelled = true;
                clearTimeout(timeoutId);
                window.removeEventListener('touchstart', cancel);
                window.removeEventListener('wheel', cancel);
            };
        }

        if (state?.preserveScroll) {
            return undefined;
        }

        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        return undefined;
    }, [pathname, search, hash, state]);

    return null;
}

export default ScrollToTop;
